import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, BrainIcon, DatabaseIcon, ShieldCheckIcon, ArrowRightIcon, ArrowDownIcon, LockIcon, FileSearchIcon, NetworkIcon, ServerIcon, LayersIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function ProductPage() {
  const navigate = useNavigate();
  const features = [{
    title: 'Delegated Authorization',
    description: 'Assess human and AI access entitlements separately. Issue ephemeral access tokens for secure, time-bound operations.',
    icon: <LockIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-396" />
  }, {
    title: 'Real-time Policy Enforcement',
    description: 'Apply deterministic decision making based on centralized policy. Enforce ingress and egress rules on data and toolchains.',
    icon: <ShieldCheckIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-397" />
  }, {
    title: 'Full Chain Auditability',
    description: 'Track exactly which human and which AI asked for specific data, and precisely what was returned to them.',
    icon: <FileSearchIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-398" />
  }, {
    title: 'Data Governance',
    description: 'Real-time data redaction, tokenization, and anonymization. Enforce data label access rules before data leaves the store.',
    icon: <LayersIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-399" />
  }, {
    title: 'MCP Gateway',
    description: 'A secure proxy layer sitting between your AI agents and your traditional data stores, acting as a context firewall.',
    icon: <NetworkIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-400" />
  }, {
    title: 'Deployment Flexibility',
    description: 'Deploy MCPGuard where you need it: in the cloud, on-premise, or in hybrid environments to meet compliance needs.',
    icon: <ServerIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-401" />
  }];
  return <div className="w-full max-w-5xl mx-auto py-12 px-4 space-y-16 animate-in fade-in duration-500" data-id="element-402">
      {/* ─── SECTION A: Hero Headline ─── */}
      <div className="bg-[#4db8a4] rounded-lg px-8 py-6" data-id="element-403">
        <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight" data-id="element-404">
          Safely scale agentic AI in highly regulated environments with
          auditable, enforceable control.
        </h1>
      </div>

      {/* ─── SECTION B: Subheadline ─── */}
      <p className="text-lg md:text-xl font-semibold text-[#1e3a5f] leading-relaxed" data-id="element-405">
        MCPGuard is the security plane for enterprise agentic AI, enabling real
        time deterministic delegated authority, full auditability, and
        regulator-defensible scaling.
      </p>

      {/* ─── SECTION C: Flow Diagram ─── */}
      <div className="bg-[#e8f7f4]/50 border border-[#4db8a4]/30 rounded-xl overflow-hidden" data-id="element-406">
        {/* Desktop: horizontal row | Mobile: vertical stack */}
        <div className="hidden md:block" data-id="element-407">
          {/* Icons + Labels + Arrows row */}
          <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] items-center gap-2 px-6 pt-6 pb-4" data-id="element-408">
            {/* Node 1: User */}
            <div className="flex flex-col items-center text-center gap-2" data-id="element-409">
              <UserIcon className="h-14 w-14 text-[#1e3a5f]" strokeWidth={1.5} data-id="element-410" />
              <span className="font-bold text-[#1e3a5f] text-lg" data-id="element-411">User</span>
            </div>
            <div className="flex items-center px-1" data-id="element-412">
              <ArrowRightIcon className="h-6 w-6 text-[#4db8a4] flex-shrink-0" data-id="element-413" />
            </div>
            {/* Node 2: AI */}
            <div className="flex flex-col items-center text-center gap-2" data-id="element-414">
              <BrainIcon className="h-14 w-14 text-[#1e3a5f]" strokeWidth={1.5} data-id="element-415" />
              <span className="font-bold text-[#1e3a5f] text-lg" data-id="element-416">Ai</span>
            </div>
            {/* Double Arrow */}
            <div className="flex flex-col items-center px-1 gap-0.5" data-id="element-417">
              <ArrowRightIcon className="h-5 w-5 text-[#4db8a4] flex-shrink-0" data-id="element-418" />
              <ArrowRightIcon className="h-5 w-5 text-[#4db8a4] flex-shrink-0 rotate-180" data-id="element-419" />
            </div>
            {/* Node 3: MCPGuard */}
            <div className="flex flex-col items-center text-center gap-2" data-id="element-420">
              <div className="bg-[#4db8a4] rounded-lg px-4 py-3 flex flex-col items-center gap-1 w-full" data-id="element-421">
                <ShieldCheckIcon className="h-12 w-12 text-white" strokeWidth={1.5} data-id="element-422" />
                <span className="font-bold text-white text-lg" data-id="element-423">MCPGuard</span>
              </div>
            </div>
            <div className="flex items-center px-1" data-id="element-424">
              <ArrowRightIcon className="h-6 w-6 text-[#4db8a4] flex-shrink-0" data-id="element-425" />
            </div>
            {/* Node 4: Traditional Data Store */}
            <div className="flex flex-col items-center text-center gap-2" data-id="element-426">
              <DatabaseIcon className="h-14 w-14 text-[#1e3a5f]" strokeWidth={1.5} data-id="element-427" />
              <span className="font-bold text-[#1e3a5f] text-lg leading-tight" data-id="element-428">
                Traditional
                <br data-id="element-429" />
                Data Store
              </span>
            </div>
          </div>

          {/* MCPGuard enforces banner */}
          <div className="bg-[#4db8a4]/10 border-t border-[#4db8a4]/30 px-6 py-2 flex items-center gap-2" data-id="element-430">
            <ShieldCheckIcon className="h-4 w-4 text-[#4db8a4] flex-shrink-0" data-id="element-431" />
            <span className="text-xs font-bold text-[#4db8a4] uppercase tracking-wider" data-id="element-432">
              MCPGuard enforces across all nodes
            </span>
          </div>

          {/* Descriptions row — aligned under each node */}
          <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-2 px-6 py-4 bg-[#4db8a4]/5" data-id="element-433">
            <p className="text-xs text-gray-600 leading-relaxed text-center" data-id="element-434">
              OIDC/Auth, Human and AI access entitlement assessed separately.
              Ephemeral Access tokens.
            </p>
            <div data-id="element-435" />
            <p className="text-xs text-gray-600 leading-relaxed text-center" data-id="element-436">
              Real time data redaction, tokenization and anonymization. Data
              label access rules enforced.
            </p>
            <div data-id="element-437" />

            <p className="text-xs text-gray-600 leading-relaxed text-left" data-id="element-438">
              Full chain auditability. Which human, which AI asked for this
              data. What was returned. Deterministic decision making based on
              Policy. Ingress and Egress rules applied. To data and toolchains.
            </p>

            <div data-id="element-439" />
            <p className="text-xs text-gray-600 leading-relaxed text-center" data-id="element-440">
              Context firewall: Did human/AI access firewall configurations,
              looking for a gap, before pulling data?
            </p>
          </div>
        </div>

        {/* Mobile: vertical stack */}
        <div className="flex flex-col items-center gap-4 md:hidden px-6 pt-6 pb-4" data-id="element-441">
          {/* Node 1: User */}
          <div className="flex flex-col items-center text-center gap-1 w-full max-w-xs" data-id="element-442">
            <UserIcon className="h-12 w-12 text-[#1e3a5f]" strokeWidth={1.5} data-id="element-443" />
            <span className="font-bold text-[#1e3a5f] text-lg" data-id="element-444">User</span>
          </div>
          <ArrowDownIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-445" />
          {/* Node 2: AI */}
          <div className="flex flex-col items-center text-center gap-1 w-full max-w-xs" data-id="element-446">
            <BrainIcon className="h-12 w-12 text-[#1e3a5f]" strokeWidth={1.5} data-id="element-447" />
            <span className="font-bold text-[#1e3a5f] text-lg" data-id="element-448">Ai</span>
          </div>
          <ArrowDownIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-449" />
          {/* Node 3: MCPGuard */}
          <div className="flex flex-col items-center text-center gap-2 w-full max-w-xs" data-id="element-450">
            <div className="bg-[#4db8a4] rounded-lg px-6 py-3 flex flex-col items-center gap-1 w-full" data-id="element-451">
              <ShieldCheckIcon className="h-10 w-10 text-white" strokeWidth={1.5} data-id="element-452" />
              <span className="font-bold text-white text-lg" data-id="element-453">MCPGuard</span>
            </div>
          </div>
          <ArrowDownIcon className="h-6 w-6 text-[#4db8a4]" data-id="element-454" />
          {/* Node 4: Traditional Data Store */}
          <div className="flex flex-col items-center text-center gap-1 w-full max-w-xs" data-id="element-455">
            <DatabaseIcon className="h-12 w-12 text-[#1e3a5f]" strokeWidth={1.5} data-id="element-456" />
            <span className="font-bold text-[#1e3a5f] text-lg" data-id="element-457">
              Traditional Data Store
            </span>
          </div>

          {/* MCPGuard enforces banner (mobile) */}
          <div className="w-full bg-[#4db8a4]/10 border border-[#4db8a4]/30 rounded-lg p-3 flex items-start gap-2 mt-2" data-id="element-458">
            <ShieldCheckIcon className="h-4 w-4 text-[#4db8a4] flex-shrink-0 mt-0.5" data-id="element-459" />
            <div className="space-y-2" data-id="element-460">
              <p className="text-xs font-bold text-[#4db8a4] uppercase tracking-wider" data-id="element-461">
                MCPGuard enforces:
              </p>
              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-462">
                <span className="font-semibold text-[#1e3a5f]" data-id="element-463">User:</span>{' '}
                OIDC/Auth, Human and AI access entitlement assessed separately.
                Ephemeral Access tokens.
              </p>
              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-464">
                <span className="font-semibold text-[#1e3a5f]" data-id="element-465">Ai:</span> Real
                time data redaction, tokenization and anonymization. Data label
                access rules enforced.
              </p>
              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-466">
                <span className="font-semibold text-[#1e3a5f]" data-id="element-467">MCPGuard:</span>{' '}
                Full chain auditability. Deterministic decision making based on
                Policy. Ingress and Egress rules applied.
              </p>
              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-468">
                <span className="font-semibold text-[#1e3a5f]" data-id="element-469">
                  Data Store:
                </span>{' '}
                Context firewall, did human/AI access firewall configurations,
                before pulling data.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ─── SECTION D: Investment Headline & Benefits ─── */}
      <div className="pt-12" data-id="element-470">
        <div className="h-px bg-gradient-to-r from-transparent via-[#4db8a4]/30 to-transparent mb-12" data-id="element-471" />
        <h2 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] leading-tight mb-8" data-id="element-472">
          You've invested heavily in AI capability, now it's time to unlock that
          investment.
        </h2>

        <div className="space-y-6" data-id="element-473">
          <p className="text-xl font-semibold text-[#4db8a4] underline underline-offset-4 decoration-[#4db8a4]" data-id="element-474">
            MCPGuard gives enables you to:
          </p>

          <ol className="space-y-4" data-id="element-475">
            <li className="flex items-start gap-4" data-id="element-476">
              <span className="font-bold text-[#4db8a4] text-lg w-7 flex-shrink-0" data-id="element-477">
                1.
              </span>
              <span className="text-lg text-gray-800" data-id="element-478">
                Deploy autonomous agents without open-ended write access
              </span>
            </li>
            <li className="flex items-start gap-4" data-id="element-479">
              <span className="font-bold text-[#4db8a4] text-lg w-7 flex-shrink-0" data-id="element-480">
                2.
              </span>
              <span className="text-lg text-gray-800" data-id="element-481">
                Demonstrate enforceable AI controls to regulators and auditors
              </span>
            </li>
            <li className="flex items-start gap-4" data-id="element-482">
              <span className="font-bold text-[#4db8a4] text-lg w-7 flex-shrink-0" data-id="element-483">
                3.
              </span>
              <span className="text-lg text-gray-800" data-id="element-484">
                Move AI out of labs and into production systems more quickly and
                securely
              </span>
            </li>
            <li className="flex items-start gap-4" data-id="element-485">
              <span className="font-bold text-[#4db8a4] text-lg w-7 flex-shrink-0" data-id="element-486">
                4.
              </span>
              <span className="text-lg text-gray-800" data-id="element-487">
                Reduce human approval bottlenecks without losing accountability
              </span>
            </li>
            <li className="flex items-start gap-4" data-id="element-488">
              <span className="font-bold text-[#4db8a4] text-lg w-7 flex-shrink-0" data-id="element-489">
                5.
              </span>
              <span className="text-lg text-gray-800" data-id="element-490">
                Understand how, when, why and by whom your AI systems are being
                used.
              </span>
            </li>
            <li className="flex items-start gap-4" data-id="element-491">
              <span className="font-bold text-[#4db8a4] text-lg w-7 flex-shrink-0" data-id="element-492">
                6.
              </span>
              <span className="text-lg text-gray-800" data-id="element-493">
                Deploy in the cloud, on-premise, or hybrid.
              </span>
            </li>
          </ol>
        </div>
      </div>

      {/* ─── SECTION E: Key Capabilities Grid ─── */}
      <div className="pt-12" data-id="element-494">
        <div className="h-px bg-gradient-to-r from-transparent via-[#4db8a4]/30 to-transparent mb-12" data-id="element-495" />
        <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8 text-center" data-id="element-496">
          Key Capabilities
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-497">
          {features.map((feature, index) => <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-498">
              <div className="h-12 w-12 bg-[#4db8a4]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-499">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-2" data-id="element-500">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-501">
                {feature.description}
              </p>
            </div>)}
        </div>
      </div>

      {/* ─── SECTION F: CTA Banner ─── */}
      <div className="bg-[#1e3a5f] rounded-2xl p-8 md:p-12 text-white text-center mt-16" data-id="element-502">
        <h2 className="text-3xl font-bold mb-6" data-id="element-503">
          Ready to secure your AI agents?
        </h2>
        <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8" data-id="element-504">
          Stop relying on open-ended write access and human approval
          bottlenecks. Implement deterministic, auditable controls today.
        </p>
        <Button size="lg" className="bg-[#4db8a4] hover:bg-[#3a9e8c] text-white border-none" onClick={() => navigate('/early-access')} data-id="element-505">
          Join the Waitlist
        </Button>
      </div>
    </div>;
}